import React, { useState, useEffect, useRef } from "react";
import {
  BoltIcon,
  BeakerIcon,
  EyeDropperIcon,
  ComputerDesktopIcon,
  TruckIcon,
  BookOpenIcon,
  CakeIcon,
  VideoCameraIcon,
} from "@heroicons/react/24/outline";

// Facilities Data
const facilities = [
  {
    id: 1,
    title: "Physics Lab",
    icon: <BoltIcon className="h-6 w-6 text-white" />,
    description:
      "Equipped with advanced instruments to enhance scientific learning.",
    image: {
      thumb:
        "https://res.cloudinary.com/ddvpczzng/image/upload/f_auto,q_auto:best,w_500,h_375,c_fill/v1757446087/Physics_lkncjq.jpg",
    },
    badge: "Lab",
    color: "bg-blue-500",
  },
  {
    id: 2,
    title: "Chemistry Lab",
    icon: <BeakerIcon className="h-6 w-6 text-white" />,
    description: "Safety-first environment with modern tools for experiments.",
    image: {
      thumb:
        "https://res.cloudinary.com/ddvpczzng/image/upload/f_auto,q_auto:best,w_500,h_375,c_fill/v1757446081/Chemistry_gwroaf.jpg",
    },
    badge: "Lab",
    color: "bg-purple-500",
  },
  {
    id: 3,
    title: "Biology Lab",
    icon: <EyeDropperIcon className="h-6 w-6 text-white" />,
    description: "Hands-on exploration of life sciences and biology models.",
    image: {
      thumb:
        "https://res.cloudinary.com/ddvpczzng/image/upload/f_auto,q_auto:best,w_500,h_375,c_fill/v1757446081/Biology_gqfw7c.jpg",
    },
    badge: "Lab",
    color: "bg-green-500",
  },
  {
    id: 4,
    title: "Computer Lab",
    icon: <ComputerDesktopIcon className="h-6 w-6 text-white" />,
    description:
      "High-speed systems and interactive software for tech learning.",
    image: {
      thumb:
        "https://res.cloudinary.com/ddvpczzng/image/upload/f_auto,q_auto:best,w_500,h_375,c_fill/v1757446076/Computer_Lab_rj9th5.jpg",
    },
    badge: "Lab",
    color: "bg-indigo-500",
  },
  {
    id: 5,
    title: "Transport",
    icon: <TruckIcon className="h-6 w-6 text-white" />,
    description: "Safe and reliable transport service with GPS tracking.",
    image: {
      thumb:
        "https://res.cloudinary.com/ddvpczzng/image/upload/f_auto,q_auto:best,w_500,h_375,c_fill/v1757446081/Transport_jvpi9b.jpg",
    },
    badge: "Transport",
    color: "bg-amber-500",
  },
  {
    id: 6,
    title: "Library",
    icon: <BookOpenIcon className="h-6 w-6 text-white" />,
    description: "Extensive collection of books and a quiet study environment.",
    image: {
      thumb:
        "https://res.cloudinary.com/ddvpczzng/image/upload/f_auto,q_auto:best,w_500,h_375,c_fill/v1757446083/IMG_3682_wfguvf.jpg",
    },
    badge: "Knowledge",
    color: "bg-red-500",
  },
  {
    id: 7,
    title: "Canteen",
    icon: <CakeIcon className="h-6 w-6 text-white" />,
    description:
      "Nutritious meals and purified water for students' well-being.",
    image: {
      thumb:
        "https://res.cloudinary.com/ddvpczzng/image/upload/f_auto,q_auto:best,w_500,h_375,c_fill/v1757446080/Canteen_z7uo1e.jpg",
    },
    badge: "Food",
    color: "bg-pink-500",
  },
  {
    id: 8,
    title: "Security",
    icon: <VideoCameraIcon className="h-6 w-6 text-white" />,
    description: "24x7 surveillance for a safe and secure campus.",
    image: {
      thumb:
        "https://res.cloudinary.com/ddvpczzng/image/upload/f_auto,q_auto:best,w_500,h_375,c_fill/v1767512564/Security_xis1kw.jpg",
        
    },
    badge: "Security",
    color: "bg-gray-700",
  },
];

const Facilities = () => {
  const [activeId, setActiveId] = useState(null);
  const cardRefs = useRef([]);

  const placeholderImg =
    "https://res.cloudinary.com/dze8favft/image/upload/f_auto,q_auto:low,w_500,h_375,c_fill/v1754344427/website%20images/Lab/Biology_Lab_1_b1xseo.jpg";

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("translate-y-0", "opacity-100");
            entry.target.classList.remove("translate-y-12", "opacity-0");
          }
        });
      },
      { threshold: 0.2 }
    );

    cardRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section className="relative w-screen left-1/2 right-1/2 mx-[-50vw] overflow-hidden py-16 md:py-20 lg:py-24 bg-[#c5935a]">
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, #64748b 1px, transparent 0)`,
          backgroundSize: "40px 40px",
        }}
      ></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mt-4">
            Our <span className="text-white">Facilities</span>
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-700 font-serif max-w-2xl mx-auto">
           Well-equipped spaces that support learning and growth
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
          {facilities.map((facility, idx) => {
            const isActive = activeId === facility.id;

            return (
              <div
                key={facility.id}
                ref={(el) => (cardRefs.current[idx] = el)}
                className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer opacity-0 translate-y-12"
                onClick={() => setActiveId(isActive ? null : facility.id)}
              >
                <div className="relative h-full flex flex-col">
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={facility.image.thumb}
                      alt={facility.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = placeholderImg;
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition duration-500"></div>

                    {/* Badge */}
                    <span
                      className={`${facility.color} absolute top-4 right-4 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg`}
                    >
                      {facility.badge}
                    </span>
                  </div>

                  {/* Card Content: Only show title */}
                  <div className="flex-1 p-6 pt-15">
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-cyan-700 transition">
                      {facility.title}
                    </h3>
                  </div>

                  {/* Hover/Active Overlay (description + icon) */}
                  <div
                    className={`
                      absolute inset-0 bg-black/60 backdrop-blur-sm rounded-2xl p-6 flex flex-col justify-center
                      transition duration-500 
                      ${
                        isActive
                          ? "opacity-100 translate-y-0"
                          : "opacity-0 translate-y-2"
                      }
                      lg:group-hover:opacity-100 lg:group-hover:translate-y-0
                    `}
                  >
                    <div className="mb-4">
                      <div
                        className={`${facility.color} w-12 h-12 rounded-lg flex items-center justify-center mb-4`}
                      >
                        {facility.icon}
                      </div>
                      <h4 className="text-xl font-bold text-white mb-2">
                        {facility.title}
                      </h4>
                      <span
                        className={`${facility.color} text-white text-xs font-bold px-3 py-1 rounded-full`}
                      >
                        {facility.badge}
                      </span>
                    </div>
                    <p className="text-gray-200 text-sm leading-relaxed">
                      {facility.description}
                    </p>
                  </div>

                  {/* Bottom Indicator */}
                  <div className="px-6 pb-4">
                    <div className="h-1 w-12 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${
                          facility.color
                        } transform transition duration-500
                          ${isActive ? "translate-x-0" : "-translate-x-full"}
                          lg:group-hover:translate-x-0
                        `}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Facilities;
